import filter from "lodash-es/filter";
import find from "lodash-es/find";
import React, { ReactNode, useState } from "react";
import { useSelector } from "react-redux";
import { useRenderOn } from "src/hooks";
import { IAppState } from "src/modules/reducers";
import styled from "styled-components";

import i18n from "../../i18n";
import { translate } from "../../services/translation.service";
import theme, { media } from "../../theme";

import {
  ILanguageFooterItemType,
  LANGUAGE_PICKER_VALUES,
} from "./LangageItem.constants";
import LanguageItem from "./LanguageItem.component";

const CurrentLang = styled.div`
  display: flex;
  align-items: center;

  ${media.laptop} {
    position: relative;
  }
`;

const Separator = styled.div`
  width: 1px;
  background-color: ${theme.colors.grey};
  margin-left: ${theme.margins.marginMedium};
  align-self: stretch;
`;

const ItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-items: center;
  padding-left: ${theme.margins.marginMedium};

  ${media.laptop} {
    position: relative;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
`;

const Image = styled.img`
  width: 24px;
  height: 24px;

  ${media.laptop} {
    margin-right: 5px;
  }
`;

const Text = styled.div`
  font-size: ${theme.fontSizes.small};

  ${media.laptop} {
    text-transform: uppercase;
    font-size: ${theme.fontSizes.tiny};
    color: ${theme.colors.white};
  }
`;

const LangPopin = styled.div`
  height: 42px;
  padding: 0 ${theme.margins.marginBase};
  background-color: ${theme.colors.black};
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  position: absolute;
  top: -100%;
  left: 0;
  height: 100%;
  width: 100%;

  ${media.laptop} {
    justify-content: center;
    padding: 0;
    top: 100%;

    &:hover {
      background-color: ${theme.colors.grey};
    }

    a,
    div {
      margin: 0;
    }
  }
`;

interface IProps {
  children?: string | ReactNode;
}

const LangPicker = ({ children }: IProps): JSX.Element => {
  const alts = useSelector((state: IAppState) => state.url?.alts);
  const [isOpenLang, setIsOpenLang] = useState(false);

  const { mdOrDown } = useRenderOn();

  const renderLangPopin = (): ReactNode | null => {
    const languageItems: ILanguageFooterItemType[] = filter(
      LANGUAGE_PICKER_VALUES,
      (value: ILanguageFooterItemType): boolean => value.value !== i18n.language
    );

    if (isOpenLang) {
      return (
        <LangPopin className="lang-popin">
          {languageItems.map(
            (
              languageItem: ILanguageFooterItemType,
              index: number
            ): ReactNode => (
              <LanguageItem
                key={`lang-item-${index}`}
                {...{ languageItem }}
                alts={alts}
              />
            )
          )}
        </LangPopin>
      );
    }

    return null;
  };

  const renderIcon = (): ReactNode => {
    const selectedLanguageItem:
      | ILanguageFooterItemType
      | undefined = find(LANGUAGE_PICKER_VALUES, { value: i18n.language });

    return (
      <Image
        src={(selectedLanguageItem && selectedLanguageItem.imgSrc) || ""}
        alt={
          selectedLanguageItem && selectedLanguageItem.value === "en"
            ? translate("global.language.englishFlag")
            : translate("global.language.frenchFlag")
        }
      />
    );
  };

  const getText = (device?: string): string => {
    const selectedLanguageItem:
      | ILanguageFooterItemType
      | undefined = find(LANGUAGE_PICKER_VALUES, { value: i18n.language });

    return (
      (selectedLanguageItem &&
        (device === "laptop"
          ? translate(selectedLanguageItem.desktopLabel)
          : translate(selectedLanguageItem.label))) ||
      ""
    );
  };

  return mdOrDown ? (
    <CurrentLang className="lang-picker">
      <ItemWrapper onClick={(): void => setIsOpenLang(!isOpenLang)}>
        {renderIcon()}
        <Text>{getText()}</Text>
        {renderLangPopin()}
      </ItemWrapper>
    </CurrentLang>
  ) : (
    <CurrentLang className="lang-picker">
      <Separator />
      <ItemWrapper onClick={(): void => setIsOpenLang(!isOpenLang)}>
        <Row>
          {renderIcon()}
          <Text>{getText("laptop")}</Text>
        </Row>
        {renderLangPopin()}
      </ItemWrapper>
    </CurrentLang>
  );
};

export default LangPicker;
